import { ZonedDate } from '@eika-infrastruktur/date';

import type {
    AccountNumber,
    BicCode,
    ChargeBearerOptions,
    CountryCode,
    CrossBorderAccountType,
    CrossBorderPriority,
    CrossBorderReceiver,
    Currency,
    NationalId,
} from '../../../../generated-types';
import { BankValidationDetails, BankValidationOptions, CrossBorderCountry, SelectCrossBorderReceiver } from './state';

export enum CrossBorderPaymentActionType {
    FROM_ACCOUNT_SELECTED = 'FROM_ACCOUNT_SELECTED',
    TO_ACCOUNT_SELECTED = 'TO_ACCOUNT_SELECTED',
    AMOUNT = 'AMOUNT',
    CURRENCY = 'CURRENCY',
    TRANSFER_CURRENCY = 'TRANSFER_CURRENCY',
    PAYMENT_DATE = 'PAYMENT_DATE',
    MESSAGE_RECEIVER = 'MESSAGE_RECEIVER',
    FIRST_BANKING_DATE_FROM_TODAY = 'FIRST_BANKING_DATE_FROM_TODAY',
    SET_PAYMENT_DESCRIPTION = 'SET_PAYMENT_DESCRIPTION',
    SET_REGULATORY_CODE = 'SET_REGULATORY_CODE',
    SET_COUNTRY_INFO = 'SET_COUNTRY_INFO',
    SET_CHOSEN_RECEIVER = 'SET_CHOSEN_RECEIVER',
    SET_RECEIVER_COUNTRY_INFO = 'SET_RECEIVER_COUNTRY_INFO',
    SET_RECEIVER_NAME = 'SET_RECEIVER_NAME',
    UPDATE_RECEIVER_NAME = 'UPDATE_RECEIVER_NAME',
    SET_RECEIVER_ACCOUNT_NUMBER = 'SET_RECEIVER_ACCOUNT_NUMBER',
    SET_RECEIVER_ADDRESS = 'SET_RECEIVER_ADDRESS',
    SET_RECEIVER_ADDRESS_2 = 'SET_RECEIVER_ADDRESS_2',
    SET_RECEIVER_POSTCODE = 'SET_RECEIVER_POSTCODE',
    SET_RECEIVER_TOWN = 'SET_RECEIVER_TOWN',
    SET_RECEIVER_COUNTRY = 'SET_RECEIVER_COUNTRY',
    SET_RECEIVER_ACCOUNT_TYPE = 'SET_RECEIVER_ACCOUNT_TYPE',
    SET_BANK_VALIDATION = 'SET_BANK_VALIDATION',
    SET_BANK_COUNTRY = 'SET_BANK_COUNTRY',
    SET_RECEIVER_NATIONAL_ID = 'SET_RECEIVER_NATIONAL_ID',
    SET_RECEIVER_BIC = 'SET_RECEIVER_BIC',
    RESET_RECEIVER = 'RESET_RECEIVER',
    RESET_BANK_VALIDATION = 'RESET_BANK_VALIDATION',
    SET_AMOUNT_IN_NOK = 'SET_AMOUNT_IN_NOK',
    SET_NEED_BIC = 'SET_NEED_BIC',
    SET_BANK_VALIDATION_OPTION = 'SET_BANK_VALIDATION_OPTION',
    SET_CHARGE_BEARER = 'SET_CHARGE_BEARER',
    SET_PRIORITY = 'SET_PRIORITY',
    RESET = 'RESET',
}

export type CrossBorderPaymentAction =
    | { type: CrossBorderPaymentActionType.FROM_ACCOUNT_SELECTED; payload: AccountNumber }
    | { type: CrossBorderPaymentActionType.TO_ACCOUNT_SELECTED; payload: SelectCrossBorderReceiver | undefined }
    | { type: CrossBorderPaymentActionType.AMOUNT; payload?: number }
    | { type: CrossBorderPaymentActionType.CURRENCY; payload?: Currency }
    | { type: CrossBorderPaymentActionType.TRANSFER_CURRENCY; payload?: Currency }
    | { type: CrossBorderPaymentActionType.PAYMENT_DATE; payload?: ZonedDate }
    | { type: CrossBorderPaymentActionType.MESSAGE_RECEIVER; payload?: string }
    | { type: CrossBorderPaymentActionType.FIRST_BANKING_DATE_FROM_TODAY; payload: ZonedDate }
    | { type: CrossBorderPaymentActionType.SET_PAYMENT_DESCRIPTION; payload?: string }
    | { type: CrossBorderPaymentActionType.SET_REGULATORY_CODE; payload?: string }
    | { type: CrossBorderPaymentActionType.SET_COUNTRY_INFO; payload: CrossBorderCountry | undefined }
    | { type: CrossBorderPaymentActionType.SET_CHOSEN_RECEIVER; payload?: CrossBorderReceiver }
    | { type: CrossBorderPaymentActionType.SET_RECEIVER_COUNTRY_INFO; payload: CrossBorderCountry | undefined }
    | { type: CrossBorderPaymentActionType.SET_RECEIVER_NAME; payload?: string }
    | { type: CrossBorderPaymentActionType.UPDATE_RECEIVER_NAME; payload?: string }
    | { type: CrossBorderPaymentActionType.SET_RECEIVER_ACCOUNT_NUMBER; payload?: string }
    | { type: CrossBorderPaymentActionType.SET_RECEIVER_ADDRESS; payload?: string }
    | { type: CrossBorderPaymentActionType.SET_RECEIVER_ADDRESS_2; payload?: string }
    | { type: CrossBorderPaymentActionType.SET_RECEIVER_POSTCODE; payload?: string }
    | { type: CrossBorderPaymentActionType.SET_RECEIVER_TOWN; payload?: string }
    | { type: CrossBorderPaymentActionType.SET_RECEIVER_COUNTRY; payload?: CountryCode }
    | { type: CrossBorderPaymentActionType.SET_RECEIVER_ACCOUNT_TYPE; payload?: CrossBorderAccountType }
    | { type: CrossBorderPaymentActionType.SET_BANK_VALIDATION; payload?: BankValidationDetails }
    | { type: CrossBorderPaymentActionType.SET_BANK_COUNTRY; payload?: CountryCode }
    | { type: CrossBorderPaymentActionType.SET_RECEIVER_NATIONAL_ID; payload?: NationalId }
    | { type: CrossBorderPaymentActionType.SET_RECEIVER_BIC; payload?: BicCode }
    | { type: CrossBorderPaymentActionType.RESET_RECEIVER }
    | { type: CrossBorderPaymentActionType.RESET_BANK_VALIDATION }
    | { type: CrossBorderPaymentActionType.SET_AMOUNT_IN_NOK; payload?: number }
    | { type: CrossBorderPaymentActionType.SET_NEED_BIC }
    | { type: CrossBorderPaymentActionType.SET_BANK_VALIDATION_OPTION; payload?: BankValidationOptions }
    | { type: CrossBorderPaymentActionType.SET_CHARGE_BEARER; payload?: ChargeBearerOptions | undefined }
    | { type: CrossBorderPaymentActionType.SET_PRIORITY; payload?: CrossBorderPriority | undefined }
    | { type: CrossBorderPaymentActionType.RESET };
